import type { IsoInstant } from '@/lib/db/types';

/**
 * The bundled New Zealand aircraft register, as it sits on disk.
 *
 * Why this exists at all: the ADS-B globes index by ICAO 24-bit
 * address and offer no registration lookup, so a pilot who wants their own
 * flights has to supply a six-digit hex code nobody memorises. New Zealand's
 * addresses are *assigned* by the CAA rather than derived from the letters of
 * the registration — there is no formula to compute `ZK-DFH → c827a6` — so the
 * only honest answer is a table, and the only complete table is the CAA's own
 * register. `docs/AIRCRAFT-REGISTER.md` has the rest of the reasoning,
 * including why no free API replaced it.
 *
 * Rows are arrays rather than objects on purpose. The register is ~6000
 * aircraft; as objects with five keys each it is about three times the bytes
 * over the wire, for a file every pilot downloads and nobody reads. `fields`
 * names the columns so the shape is self-describing anyway, and
 * `parseRegister` checks it rather than trusting the file's order.
 */

export const REGISTER_FIELDS = ['registration', 'icaoHex', 'make', 'model', 'category'] as const;

/** One row, in `REGISTER_FIELDS` order. An empty string means the register had nothing. */
export type RegisterRow = readonly [string, string, string, string, string];

export interface AircraftRegisterFile {
  /** Where the CSV was downloaded from, so a stale file can be traced to its source. */
  source: string;
  /** When the updater last read that CSV. */
  retrievedAt: IsoInstant;
  /** `Last-Modified` from the CAA, where they sent one — the register's own date, not ours. */
  sourceLastModified?: IsoInstant;
  count: number;
  fields: readonly string[];
  aircraft: readonly RegisterRow[];
}

/** One aircraft as the app uses it: absent fields are absent, not empty strings. */
export interface RegisterEntry {
  /** `ZK-XXX`, normalised the same way `aircraft.registration` is. */
  registration: string;
  /** ICAO 24-bit address, six lowercase hex digits. */
  icaoHex?: string;
  make?: string;
  model?: string;
  /** The CAA's own "Model Category" wording, kept verbatim — it is what `suggestClass` reads. */
  category?: string;
}

export interface AircraftRegister {
  source: string;
  retrievedAt: IsoInstant;
  sourceLastModified?: IsoInstant;
  /** Empty when the register has never been downloaded for this build. */
  entries: ReadonlyMap<string, RegisterEntry>;
}
